import { type FC, type JSX } from 'react'
import {
  FormattedMessage,
  type IntlShape,
  type PrimitiveType,
  useIntl,
} from 'react-intl'

import { type PimProductV11 } from '@redteclab/api/clients/bully'

import {
  EXPERIMENTS_TRACK_EVENT_PDP_DELIVERY_OPTIONS_NOW_CC,
  EXPERIMENTS_TRACK_EVENT_PDP_DELIVERY_OPTIONS_NOW_DELIVERY,
  experimentsTrackEvent,
} from '../../../../experiments/eventTracking'
import { useGetExperiment } from '../../../../experiments/hooks'
import { EXPERIMENT_NAME_PDP_DELIVERY_OPTIONS } from '../../../../experiments/model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../../../../experiments/model/EXPERIMENT_VARIATION'
import { useGlobalConfigContext } from '../../../../global-config'
import {
  nowBasicCheckRespFirstSlotNoShippingCostsIfTotalMinimum,
  nowBasicCheckRespGetSlotShippingCostAmountDefault,
  nowBasicCheckResponseAccessorIsCurrentVariantEligibleForDelivery,
  nowBasicCheckResponseAccessorIsCurrentVariantEligibleForPickup,
  useIsNowEnabled,
} from '../../../../now'
import { priceGetFormattedPrice } from '../../../../price'
import {
  ProductPageNowDeliveryDetailsDialog,
  type ProductPageNowDeliveryDetailsDialogOpenableControls,
} from '../../../now/components/ProductPageNowDeliveryDetailsDialog/ProductPageNowDeliveryDetailsDialog'
import {
  PRODUCT_PAGE_NOW_DELIVERY_DETAILS_DIALOG_TAB_DELIVERY,
  PRODUCT_PAGE_NOW_DELIVERY_DETAILS_DIALOG_TAB_PICKUP,
  type ProductPageNowDetails,
} from '../../product-page-context/useProductPageNowDetails'
import { useProductPageContext } from '../../productPageContext'
import { ProductBuyBoxDeliveryOption } from '../ProductBuyBoxNowDeliveryOption'

import { ProductBuyBoxNowDeliveryOptionDeliveryMessage } from './ProductBuyBoxNowDeliveryOptionDeliveryMessage'
import { ProductBuyBoxNowDeliveryOptionPickupMessage } from './ProductBuyBoxNowDeliveryOptionPickupMessage'

type DeliveryInfoMessageProps = {
  currentVariant: PimProductV11
  intl: IntlShape
  nowDetails: ProductPageNowDetails
  pdpDeliveryOptionsVariant: EXPERIMENT_VARIATION | undefined
}

const getDeliveryAdditionalInfoMessage = ({
  intl,
  nowDetails,
  pdpDeliveryOptionsVariant,
}: DeliveryInfoMessageProps): JSX.Element | null => {
  const deliveryDefaultShippingCostAmount =
    nowBasicCheckRespGetSlotShippingCostAmountDefault(
      nowDetails.nowDataPerProductCurrentVariantResponse?.slots,
    )

  if (deliveryDefaultShippingCostAmount === undefined) {
    return null
  }

  const deliveryFreeShippingCosts =
    nowBasicCheckRespFirstSlotNoShippingCostsIfTotalMinimum(
      nowDetails.nowDataPerProductCurrentVariantResponse?.slots,
    )

  let values: Record<string, PrimitiveType> = {
    amount: priceGetFormattedPrice(deliveryDefaultShippingCostAmount, intl),
  }

  if (deliveryFreeShippingCosts === 0) {
    return (
      <FormattedMessage
        id="now.product.shippingCostsUnconditional"
        values={values}
      />
    )
  }

  values = {
    ...values,
    freeShippingAmount: priceGetFormattedPrice(deliveryFreeShippingCosts, intl),
  }

  return pdpDeliveryOptionsVariant === EXPERIMENT_VARIATION.V1 ||
    pdpDeliveryOptionsVariant === EXPERIMENT_VARIATION.V2 ? (
    <FormattedMessage id="now.product.shippingCosts" values={values} />
  ) : (
    <FormattedMessage id="now.product.shippingShort" values={values} />
  )
}

const getModalControls = (
  nowDetails: ProductPageNowDetails,
): ProductPageNowDeliveryDetailsDialogOpenableControls => {
  const handleClose = (): void => {
    nowDetails.setProductPageNowDeliveryDetailsDialogActiveTab('')
  }

  return {
    handleClose,
    isOpen:
      nowDetails.productPageNowDeliveryDetailsDialogActiveTab ===
        PRODUCT_PAGE_NOW_DELIVERY_DETAILS_DIALOG_TAB_PICKUP ||
      nowDetails.productPageNowDeliveryDetailsDialogActiveTab ===
        PRODUCT_PAGE_NOW_DELIVERY_DETAILS_DIALOG_TAB_DELIVERY,
  }
}

export const ProductBuyBoxNowDeliveryOptions: FC = () => {
  const globalContext = useGlobalConfigContext()
  const intl = useIntl()
  const { currentVariant, nowDetails } = useProductPageContext()
  const isNowEnabled = useIsNowEnabled()

  const pdpDeliveryOptions = useGetExperiment(
    EXPERIMENT_NAME_PDP_DELIVERY_OPTIONS,
  )

  const deliveryAdditionalInfoMessage = getDeliveryAdditionalInfoMessage({
    currentVariant,
    intl,
    nowDetails,
    pdpDeliveryOptionsVariant: pdpDeliveryOptions.variant,
  })

  const nowEligibleDelivery =
    nowBasicCheckResponseAccessorIsCurrentVariantEligibleForDelivery(
      nowDetails.nowDataPerProductCurrentVariantResponse,
    )
  const nowEligiblePickup =
    nowBasicCheckResponseAccessorIsCurrentVariantEligibleForPickup(
      nowDetails.nowDataPerProductCurrentVariantResponse,
    )

  if (!isNowEnabled || (!nowEligibleDelivery && !nowEligiblePickup)) {
    return null
  }

  return (
    <div className="my-2">
      {nowEligibleDelivery && deliveryAdditionalInfoMessage ? (
        <ProductBuyBoxDeliveryOption
          additionalInfo={deliveryAdditionalInfoMessage}
          dotColor="bg-dark-hover-success"
          infoIconHandleClick={() => {
            nowDetails.setProductPageNowDeliveryDetailsDialogActiveTab(
              PRODUCT_PAGE_NOW_DELIVERY_DETAILS_DIALOG_TAB_DELIVERY,
            )
            experimentsTrackEvent(
              globalContext,
              EXPERIMENTS_TRACK_EVENT_PDP_DELIVERY_OPTIONS_NOW_DELIVERY,
            )
          }}
          message={<ProductBuyBoxNowDeliveryOptionDeliveryMessage />}
        />
      ) : null}
      {nowEligiblePickup ? (
        <ProductBuyBoxDeliveryOption
          additionalInfo={
            <FormattedMessage id="now.product.shippingCostsFree" />
          }
          dotColor="bg-dark-hover-success"
          infoIconHandleClick={() => {
            nowDetails.setProductPageNowDeliveryDetailsDialogActiveTab(
              PRODUCT_PAGE_NOW_DELIVERY_DETAILS_DIALOG_TAB_PICKUP,
            )
            experimentsTrackEvent(
              globalContext,
              EXPERIMENTS_TRACK_EVENT_PDP_DELIVERY_OPTIONS_NOW_CC,
            )
          }}
          message={<ProductBuyBoxNowDeliveryOptionPickupMessage />}
        />
      ) : null}
      <ProductPageNowDeliveryDetailsDialog
        controls={getModalControls(nowDetails)}
      />
    </div>
  )
}
